/**
 * A change to one exercise, drawn as the builder's own card (ai-plan-builder R15; board U). The
 * proposal hosts draw it inside Buddy's proposal card: owner, 7 October 2026, „to powinno pokazywać
 * kartę ćwiczenia jak było i jak jest w takiej formie jak w builderze".
 *
 * It is `ExerciseBlock`'s head and `SetTable` in their read-only form, over the exercise as the change
 * writes it; `model/exerciseChange.ts` decides what is marked. Nothing here is a control: the card is
 * read, and the proposal around it holds the decisions. The foot is the host's progression statement
 * (`features/plan-progression`), a slot for the same reason `ExerciseBlock`'s is.
 */

import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ExerciseDraft } from '~/entities/plan-draft';

import { useExerciseCatalogue } from '../lib/useExerciseCatalogue';
import { exerciseChangeView, type ExerciseChangeBaseline } from '../model/exerciseChange';

import { ExerciseIdentity } from './ExerciseIdentity';
import { SetTable } from './SetTable';
import blockStyles from './ExerciseBlock.module.css';
import styles from './ExerciseChangeCard.module.css';

export interface ExerciseChangeCardProps {
  /** The exercise as the change writes it. */
  readonly exercise: ExerciseDraft;
  /** What it is compared with: the exercise it alters, nothing (every set new), or nothing to mark. */
  readonly baseline: ExerciseChangeBaseline;
  /** The builder's marker for its place in the day (`2`, `2b`); `null` for a warm-up or when unknown. */
  readonly ordinal: string | null;
  /** The day's own rest, in seconds; `null` for the plan's default. */
  readonly dayRestSeconds: number | null;
  /** The head's right end: „4 → 5 serii", „Nowe w miejscu 2". `null` for none. */
  readonly tag: string | null;
  /** The card's foot: the progression statement. `null` for none. */
  readonly progression: ReactNode;
}

/** Nothing on a change under review acts, so nothing is announced from inside its table. */
function nothingToAnnounce(): void {
  return undefined;
}

export function ExerciseChangeCard({ exercise, baseline, ordinal, dayRestSeconds, tag, progression }: ExerciseChangeCardProps) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const catalogue = useExerciseCatalogue();
  const view = exerciseChangeView({ exercise, baseline, ordinal, dayRestSeconds, ...catalogue, t, locale: i18n.language });

  return (
    <div
      className={`${String(blockStyles.block)} ${String(styles.card)}`}
      // A warm-up is drawn as the builder draws one: its smaller still and its own marker ink.
      data-warmup={view.exercise.isWarmup ? '' : undefined}
    >
      <div className={blockStyles.head}>
        <ExerciseIdentity exercise={view.exercise} headingId={headingId} isGrouped={false} reserve />
        {tag !== null && <span className={styles.tag}>{tag}</span>}
      </div>
      <div className={blockStyles.main}>
        <SetTable
          exercise={view.exercise}
          dayRestSeconds={0}
          labelledBy={headingId}
          announce={nothingToAnnounce}
          readOnly
          changes={view.rows}
        />
      </div>
      {progression !== null && (
        <div className={blockStyles.foot} data-columns="one">
          {progression}
        </div>
      )}
    </div>
  );
}
